'use client'
import {createAsyncThunk} from "@reduxjs/toolkit";
import $http from "@/app/_api";
import RouterMgr from "@/Core/RouterMgr";
import {
    getLocalRewardType,
    getLoginType,
    getUserId,
    removeAuthInfo,
    removeLocalLogout,
    setLocalLogout,
    setLocalRewardType,
    setLoginType,
    setToken,
    setTokenExpireTime,
    setUserId,
    setUuidChange
} from "@/app/utils/auth";
import {
    LoginType, RESET_USER_INFO,
    SET_ADD_DESKTOP_OPEN,
    SET_GUIDE_OPEN,
    SET_IS_ADD_DESKTOP_OPEN,
    SET_IS_LOGGED_OUT,
    SET_REWARD_TYPE
} from "@/app/userSlice";
import {SDialogManagerFC} from "@/Master/SDialogFC/SDialogManagerFC";
import {AccountSelectionDialogFC} from "@/app/_dialogs/AccountSelectionDialog/AccountSelectionDialog";
import {PrivacyDialogFC} from "@/app/_dialogs/PrivacyDialog/PrivacyDialog";
import {AppThunk} from "@/app/store";
import {RewardType} from "@/Core/Reward/RewardInfo";
import {updateAccountAsync} from "@/app/(pages)/account/accountAPI";
import DataMgr from "@/Fwk/DataMgr";
import {fwkConst} from "@/Fwk/FwkConst";
import KitPwa from "@/Master/PWA/KitPwa";
import Util from "@/Master/Util";
import ChannelMgr from "@/Fwk/Channel/ChannelMgr";
import GameTrack from "@/Core/GameTrack";
import AuthTrack, {SignUpScene} from "@/Core/AuthTrack";

export const userLoginAsync = createAsyncThunk(
    'user/login',
    async (params: any, ThunkAPI) => {

        if (ChannelMgr.channelQid){
            params.channelQid = ChannelMgr.channelQid;
        }
        if (ChannelMgr.channelAttributionData){
            params.attribution = ChannelMgr.channelAttributionData;
        }

        const response = await $http.Auth.userLogin(params);
        // 登陆成功之后清除访客登陆信息
        if (response) {
            AuthTrack.clickLoginSuccess();
            removeLocalLogout();
            removeAuthInfo();
            ThunkAPI.dispatch(SET_IS_LOGGED_OUT(false));
            ThunkAPI.dispatch(RESET_USER_INFO());
        }
        setToken(response.token);
        setUserId(response.id);
        setTokenExpireTime(response.expireTime);
        setLoginType(LoginType.normal);
        setUuidChange(response.uuidChange);

        if (response.attribution){
            ChannelMgr.setAttributionDataAsync(response.attribution)
        }

        ThunkAPI.dispatch(getUserInfoAsync());
        // jump to home page.
        RouterMgr.home();
        return response;
    }
)

export const userSignUpAsync = createAsyncThunk(
    'user/signUp',
    async (params: any, thunkAPI) => {
        const response = await $http.Auth.userSignUp(params);

        // 埋点
        if (response) {
            if (params.isFromPayOk) {
                AuthTrack.clickSignUpSuccess(SignUpScene.HasRecharge)
            } else {
                AuthTrack.clickSignUpSuccess(SignUpScene.DidNotRecharge)
            }

            removeAuthInfo();

            // 注册成功自动登录
            let reqParams = {
                mobile: params.mobile,
                areaCode: params.areaCode,
                password: params.password,
            };
            thunkAPI.dispatch(userLoginAsync(reqParams));
        }

        return response;
    }
)

export const visitorLoginAsync = createAsyncThunk(
    'user/visitorLogin',
    async (params: any, ThunkApi) => {
        const lastUserId = getUserId();
        if (lastUserId){
            params.userId = lastUserId;
        }
        params.timeZone = Util.GetTimeZone;//todo 后面由后端直接设置

        if (ChannelMgr.channelQid){
            params.channelQid = ChannelMgr.channelQid;
        }
        if (ChannelMgr.channelAttributionData){
            params.attribution = ChannelMgr.channelAttributionData;
        }
        const response = await $http.Auth.visitorLogin(params);

        setToken(response.token);
        setUserId(response.id);
        setTokenExpireTime(response.expireTime);
        setLoginType(LoginType.visitor);
        setUuidChange(response.uuidChange);

        if (response.attribution){
            ChannelMgr.setAttributionDataAsync(response.attribution);
        }

        ThunkApi.dispatch(getUserInfoAsync());
        return response;
    }
)

export const userSignOutAsync = createAsyncThunk(
    'user/signOut',
    async (params: any, ThunkApi) => {
        const response = await $http.Account.userSignOut();
        setLocalLogout();
        removeAuthInfo();
        RouterMgr.login([['form', 'signOut']]);
        ThunkApi.dispatch(SET_IS_LOGGED_OUT(true));
        return response;
    }
)

export const userResetPwdAsync = createAsyncThunk(
    'user/resetPwd',
    async (params: any, ThunkApi) => {
        const response = await $http.Auth.userResetPassword(params);

        // 重置成功自动登录
        const reqParams = {
            mobile: params.mobile,
            areaCode: params.areaCode,
            password: params.newPassword,
        };
        ThunkApi.dispatch(userLoginAsync(reqParams));

        return response;
    }
)

//获取帐户基础信息。使用这个不影响㊗主流程
export const getUserInfoGameBaseAsync = createAsyncThunk(
    'user/getUserInfoByUnityGame',
    async (params, ThunkApi) => {
        const response = await $http.Account.getUserInfo();

        // 获取会员基础信息
        const userParams = {
            vipExp: response.baseInfo.vipExp,
            vipLevel: response.baseInfo.vipLevel
        }
        ThunkApi.dispatch(updateAccountAsync(userParams))

        return response;
    }
)

export const getUserInfoAsync = createAsyncThunk(
    'user/getUserInfo',
    async (params, thunkAPI) => {
        const response = await $http.Account.getUserInfo();
        // 访客新手引导流程
        if (!response.gameRewardInfo.rewardRecord.starter && getLoginType() === LoginType.visitor) {
            GameTrack.showWelcome();
            SDialogManagerFC.openDialog(AccountSelectionDialogFC, {
                fcKey: SDialogManagerFC.getFCKey(AccountSelectionDialogFC),
                callToLogin: () => {
                    GameTrack.closeWelcome();
                    GameTrack.showPrivacy();
                    SDialogManagerFC.openDialog(PrivacyDialogFC, {
                        fcKey: SDialogManagerFC.getFCKey(PrivacyDialogFC),
                        callAccept: () => {
                            GameTrack.clickAgreePrivacy();
                            thunkAPI.dispatch(SET_GUIDE_OPEN(true))
                        },
                        onClose: () => {
                            GameTrack.clickClosePrivacy()
                            thunkAPI.dispatch(SET_GUIDE_OPEN(true))
                        }
                    })
                }
            });
        }

        // 注册用户新手引导流程
        if (!response.gameRewardInfo.rewardRecord.starter && getLoginType() === LoginType.normal) {
            SDialogManagerFC.openDialog(PrivacyDialogFC, {
                fcKey: SDialogManagerFC.getFCKey(PrivacyDialogFC),
                callAccept: () => {
                    thunkAPI.dispatch(SET_GUIDE_OPEN(true))
                },
                onClose: () => {
                    thunkAPI.dispatch(SET_GUIDE_OPEN(true))
                }
            })
        }

        // 设置奖励模式
        if (getLocalRewardType()) {
            console.log(getLocalRewardType())
            // setLocalRewardType(RewardType.GC);
            thunkAPI.dispatch(SET_REWARD_TYPE(getLocalRewardType()));
        }

        // 获取会员基础信息
        const userParams = {
            vipExp: response.baseInfo.vipExp,
            vipLevel: response.baseInfo.vipLevel
        }
        thunkAPI.dispatch(updateAccountAsync(userParams))

        return response;
    }
)
